import React from 'react';
import { FormikErrors } from 'formik';
import styled, { css } from 'styled-components';

import { ManualCSSColors } from 'app/shared/theme';
import { useCurrentTheme } from 'app/shared/theme';
import { WithSpacingProps } from 'app/shared/components/styleUtils';
import { FlexDivWithSpacing } from 'app/shared/components/atoms/FlexDivWithSpacing';
import { StreamlineIcon } from 'app/shared/components/atoms/StreamlineIcon';
import {
  Caption,
  FormGroupLabel,
} from 'app/shared/components/atoms/TypographyManualCSS';
import { ReactComponent as AlertCircle } from 'icons/streamline-regular/interface-essential/alerts/alert-circle.svg';

interface Props extends WithSpacingProps {
  label?: React.ReactNode;
  uppercaseLabel?: boolean;
  required?: boolean;
  errorMsg?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  children: React.ReactElement<{ hasError?: boolean } & any>;
  renderIcon?: Function;
  'data-qaid'?: string;
  id?: string;
  name?: string;
  className?: string;
  displayIconWithError?: boolean;
  errorColor?: ManualCSSColors;
  labelColor?: string;
}

const StyledSpan = styled.span<{
  hasError: boolean;
  errorColor: ManualCSSColors;
}>`
  ${({ theme, hasError, errorColor }) => css`
    color: ${hasError ? theme.colors[errorColor] : theme.colors.secondaryText};
  `}
`;

const FormGroup: React.FC<React.PropsWithChildren<Props>> = ({
  label,
  uppercaseLabel = false,
  required,
  children,
  errorMsg,
  renderIcon,
  'data-qaid': qaId,
  id,
  name,
  className,
  displayIconWithError,
  errorColor = 'redRedWine',
  labelColor,
  ...spacingProps
}) => {
  const themeContext = useCurrentTheme();

  const renderLabel = () => {
    if (!label) {
      return null;
    }

    return (
      <FormGroupLabel
        htmlFor={name}
        uppercase={uppercaseLabel}
        color={
          errorMsg
            ? themeContext.colors[errorColor]
            : labelColor
              ? labelColor
              : undefined
        }
        data-qaid={`${qaId}-label`}
      >
        <span>{label}</span>
        {required ? (
          <StyledSpan hasError={!!errorMsg} errorColor={errorColor}>
            *
          </StyledSpan>
        ) : null}{' '}
        {renderIcon ? renderIcon() : null}
      </FormGroupLabel>
    );
  };

  return (
    <FlexDivWithSpacing
      className={className}
      id={id}
      data-qaid={qaId}
      {...spacingProps}
    >
      {renderLabel()}
      {React.cloneElement(children, { hasError: !!errorMsg })}
      {errorMsg && (
        <FlexDivWithSpacing gap={1} pt={2} alignItems="center">
          {displayIconWithError && (
            <StreamlineIcon
              size={14}
              display="inline-flex"
              icon={AlertCircle}
              stroke={themeContext.colors[errorColor]}
            />
          )}
          <Caption
            color={themeContext.colors[errorColor]}
            data-qaid={`${qaId}-error`}
          >
            {errorMsg}
          </Caption>
        </FlexDivWithSpacing>
      )}
    </FlexDivWithSpacing>
  );
};

export default FormGroup;
